<script setup lang="ts">
  import { computed, onBeforeUnmount, reactive, ref, watch, watchEffect } from 'vue'
  import { Form, Input, InputNumber, Select, SelectOption, Button } from 'ant-design-vue'
  import { CustomReportSchema } from '../models/CustomReportSchema'
  import { CustomReportField } from '../models/CustomReportField'
  import { CustomReportFieldType } from '../models/CustomReportFieldType'
  import { CustomReportsService } from '../services/CustomReportsService'
  import { debounceTime, Subject } from 'rxjs'
  import { parseSmartValue } from '@/utils'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import { CompressOutlined, ExpandOutlined } from '@ant-design/icons-vue'
  import RegexCustomReportConfiguration from './RegexCustomReportConfiguration.vue'
  import CustomReportHelpPopover from '../help/components/CustomReportHelpPopover.vue'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { PostProcessingOutput } from '@/models/PostProcessingOutput'
  import { PostProcessingOutputPlot } from '@/models/PostProcessingOutputPlot'
  import { PostProcessingOutputTable } from '@/models/PostProcessingOutputTable'
  import CustomReportSelectMappingConfiguration from './CustomReportSelectMappingConfiguration.vue'
  import ArrayValuesCustomReportConfiguration from './ArrayValuesCustomReportConfiguration.vue'
  import ColorCustomReportConfiguration from './ColorCustomReportConfiguration.vue'
  import SelectOrCreateCustomReportConfiguration from './SelectOrCreateCustomReportConfiguration.vue'
  import JsonCustomReportConfiguration from './JsonCustomReportConfiguration.vue'
  import { CustomReportJsonReportConfigurationService } from '../services/CustomReportJsonReportConfigurationService'

  import { CustomReportsUiService } from '../services/CustomReportsUiService'
  import { CustomReport } from '../models/CustomReport'

  interface IObjectCustomReportBuilderProps {
    customReportSchemas: Array<CustomReportSchema>
    customReportField: CustomReportField
    customPlotsOutput?: PostProcessingOutput<PostProcessingOutputPlot>
    customTablesOutput?: PostProcessingOutput<PostProcessingOutputTable>
    modelValue: {}
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    rootCustomReport?: CustomReport | null
    currentPath?: string[]
  }

  interface IObjectCustomReportBuilderEmits {
    (e: 'update:modelValue', value: {}): void
  }

  const props = defineProps<IObjectCustomReportBuilderProps>()
  const emit = defineEmits<IObjectCustomReportBuilderEmits>()

  const modelValue = reactive<{}>(props.modelValue)
  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (props.customReportField.field_type !== CustomReportFieldType.Object) return
    if (
      (props.customReportField.item_schema == null && props.customReportField.fields == null) ||
      (props.customReportField.item_schema != null &&
        props.customReportField.item_schema.field_type !== CustomReportFieldType.Object)
    )
      throw new Error(
        'ObjectCustomReportBuilder requires an Object type with fields or child items_schema of type'
      )

    if (props.customReportField.fields != null) {
      configuration.value = CustomReportsService.initializeCustomReportFieldConfiguration(
        props.customReportField.fields!
      )
    } else if (props.customReportField.item_schema != null) {
      configuration.value = CustomReportsService.initializeCustomReportConfigurationForField(
        props.customReportField.item_schema
      )
    }
  }

  const handleConfigurationChange = () => {
    if (props.customReportField) {
      emit('update:modelValue', configuration.value)
    }
  }

  // @ts-expect-error
  const handleCustomReportTypeChanged = (customReportType: string, value: {}) => {
    //TODO: Support object nested custom reports and type conversion
    //if (props.customReport) {
    //emit('configuration-updated', { ...props.customReport, type: customReportType }, value, true)
    //}
  }

  const onInputChange = (newValue: string, fieldName: string) => {
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()

  const inputValueChangedSubscription = inputValueChanged$
    .pipe(debounceTime(1000))
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  watch(
    modelValue,
    (newVal) => {
      configuration.value = { ...newVal }
    },
    { immediate: true }
  )

  watch(
    () => props.customReportField,
    (newCustomReportField) => {
      if (newCustomReportField) {
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
      }
    },
    { immediate: true }
  )

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  const simpleFieldCount = computed(() =>
    props?.customReportField?.fields
      ? props.customReportField.fields.filter(
          (f: any) =>
            f.field_type !== CustomReportFieldType.Object &&
            f.field_type !== CustomReportFieldType.Array &&
            f.field_type !== CustomReportFieldType.Json
        ).length
      : 0
  )

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.customReportField?.fields?.findIndex(
        (x: CustomReportField) =>
          x.field_type === CustomReportFieldType.Array ||
          x.field_type === CustomReportFieldType.Object ||
          x.field_type === CustomReportFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: CustomReportField): boolean => {
    return (
      field.field_type !== CustomReportFieldType.Object &&
      field.field_type !== CustomReportFieldType.Json
    )
  }

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const isObjectWithNestedObject = computed(
    () => props.customReportField.fields == null && props.customReportField.item_schema != null
  )

  const { controlSize } = useWindowSize()

  const displayField = computed(() => {
    const mappedField = props.customReportField?.display_field as
      | string
      | string[]
      | null
      | undefined
    if (mappedField == null) return undefined

    const fields = props.customReportField?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onBeforeUnmount(() => {
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div v-if="isObjectWithNestedObject" class="object-customReport-builder-grid-span">
    <ObjectCustomReportBuilder
      :configuration-level="props.configurationLevel"
      v-model="configuration[props.customReportField.item_schema!.name]"
      :custom-report-field="props.customReportField.item_schema!"
      :custom-plots-output="customPlotsOutput"
      :custom-tables-output="customTablesOutput"
      @update:modelValue="handleConfigurationChange"
      :customReportSchemas="customReportSchemas"
      :is-condensed-layout="props.isCondensedLayout"
      :global-collapsed="globalCollapsed"
      :root-custom-report="props.rootCustomReport"
    />
  </div>

  <div v-else>
    <div
      v-if="customReportField"
      :class="[
        'border',
        'customReport-configuration',
        'h-full',
        'w-full',
        {
          'child-customReport-configuration': props.configurationLevel > 1,
          '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
          '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0
        }
      ]"
    >
      <div class="flex h-full flex-col">
        <div class="horizontal-rule mb-4 border-b pb-1">
          <div class="flex">
            <div>
              <h3 class="mb-1 text-lg font-semibold">
                Configure: {{ customReportField.display_name }}
                <span v-if="displayField != null">{{ `(${displayField})` }}</span>
              </h3>
            </div>

            <div class="ml-auto flex" v-if="props.configurationLevel > 1">
              <a-tooltip placement="top">
                <template #title>Toggle Collapse</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  class="flex items-center justify-center"
                  @click="toggleCollapse"
                  aria-label="Toggle Collapse"
                >
                  <PlusSquare
                    v-if="isCollapsed"
                    class="text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
                </Button>
              </a-tooltip>

              <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
                <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  size="large"
                  class="flex items-center justify-center"
                  @click="toggleGlobalCollapse"
                  aria-label="Toggle Collapse All"
                >
                  <CompressOutlined
                    v-if="isCollapsed"
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <ExpandOutlined
                    v-else
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                </Button>
              </a-tooltip>
            </div>
          </div>
        </div>

        <div
          :class="[
            'flex-1',
            'overflow-y-auto',
            { 'collapsed-customReport-configuration': isCollapsed }
          ]"
        >
          <Form ref="formRef" :model="configuration" layout="vertical">
            <div
              :class="{
                'object-customReport-fields-grid': true,
                expanded: !props.isCondensedLayout
              }"
            >
              <div
                v-for="field in customReportField.fields"
                :key="field.name"
                class="object-customReport-form-field-grid"
                :class="{
                  'array-customReport-builder-grid-span':
                    field.field_type === CustomReportFieldType.Array,
                  'object-customReport-builder-grid-span':
                    field.field_type === CustomReportFieldType.Object,
                  'json-customReport-configuration-grid-span':
                    field.field_type === CustomReportFieldType.Json,
                  'single-field-grid-span':
                    simpleFieldCount <= 1 &&
                    field.field_type !== CustomReportFieldType.Object &&
                    field.field_type !== CustomReportFieldType.Array &&
                    field.field_type !== CustomReportFieldType.Json,
                  '!hidden': !CustomReportsUiService.isFieldVisibleFromRoot(
                    field,
                    props.rootCustomReport,
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  )
                }"
              >
                <div class="flex">
                  <div v-if="shouldShowFieldInformation(field)" class="min-w-0 flex-1">
                    <label class="customReport-field-label mb-1 block text-sm font-medium">
                      {{ field.display_name }}
                      <span v-if="field.required" class="text-red-500">*</span>
                    </label>
                    <p v-if="field.description" class="customReport-field-label mb-2 text-xs">
                      {{ field.description }}
                    </p>
                  </div>
                  <div
                    v-if="field.help_content != null && field.help_content?.length > 0"
                    class="m1-auto flex justify-end"
                  >
                    <CustomReportHelpPopover
                      :content-type="field.help_content"
                      :title="`${field.display_name} Help`"
                    />
                  </div>
                </div>

                <!-- Text Input -->
                <Input
                  v-if="field.field_type === CustomReportFieldType.Text"
                  :value="configuration[field.name]"
                  @update:value="(val: string) => onInputChange(val, field.name)"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  :placeholder="field.placeholder || ''"
                  :size="controlSize"
                />

                <!-- Number Input -->
                <InputNumber
                  v-else-if="field.field_type === CustomReportFieldType.Number"
                  v-model:value="configuration[field.name]"
                  :placeholder="field.placeholder || ''"
                  :size="controlSize"
                  @change="handleConfigurationChange"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                />

                <!-- Regex Input -->
                <RegexCustomReportConfiguration
                  v-else-if="field.field_type === CustomReportFieldType.RegularExpression"
                  v-model="configuration[field.name]"
                  :required="field.required"
                  :regex="field.field_type_args?.regex_pattern"
                  :data-type="field.field_type_args?.regex_data_type"
                  :placeholder="field.placeholder || ''"
                  :error-message="field.field_type_args?.regex_description"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select Dropdown -->
                <Select
                  v-else-if="field.field_type === CustomReportFieldType.Select"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Select Dropdown JSON Payload -->
                <Select
                  v-else-if="field.field_type === CustomReportFieldType.SelectJson"
                  :value="JSON.stringify(configuration[field.name])"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @update:value="
                    (val: string) => {
                      try {
                        const parsed = JSON.parse(val)
                        configuration[field.name] = parsed
                        handleConfigurationChange()
                      } catch (e) {
                        console.error('Failed to parse selected JSON value', e)
                      }
                    }
                  "
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="JSON.stringify(option.value)"
                    :value="JSON.stringify(option.value)"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Select Dropdown Or Create -->
                <SelectOrCreateCustomReportConfiguration
                  v-else-if="field.field_type === CustomReportFieldType.SelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()}`"
                  :options="field.options!"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi-Select -->
                <Select
                  v-else-if="field.field_type === CustomReportFieldType.MultiSelect"
                  v-model:value="configuration[field.name]"
                  mode="multiple"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-if="field.field_type === CustomReportFieldType.MultiSelect"
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </SelectOption>
                </Select>

                <!-- Boolean Checkbox -->
                <Checkbox
                  v-else-if="field.field_type === CustomReportFieldType.Boolean"
                  @change="handleConfigurationChange"
                  v-model:checked="configuration[field.name]"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  :class="checkboxSizeClass"
                >
                  {{ field.display_name }}
                </Checkbox>

                <!-- Array Values -->
                <ArrayValuesCustomReportConfiguration
                  v-else-if="field.field_type === CustomReportFieldType.ArrayValues"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder || 'Enter value(s)'"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Color Picker -->
                <ColorCustomReportConfiguration
                  v-else-if="field.field_type === CustomReportFieldType.ColorPicker"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder || 'Select color'"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select/MultiSelect Mapping -->
                <CustomReportSelectMappingConfiguration
                  v-else-if="
                    field.field_type === CustomReportFieldType.SelectMapping ||
                    field.field_type === CustomReportFieldType.MultiSelectMapping
                  "
                  v-model="configuration[field.name]"
                  :field="field"
                  :placeholder="field.placeholder"
                  :local-configuration="configuration"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Object Type -->
                <ObjectCustomReportBuilder
                  v-else-if="field.field_type === CustomReportFieldType.Object"
                  :configuration-level="props.configurationLevel + 1"
                  v-model="configuration[field.name]"
                  :custom-report-field="field"
                  :custom-plots-output="customPlotsOutput"
                  :custom-tables-output="customTablesOutput"
                  @update:modelValue="handleConfigurationChange"
                  :customReportSchemas="customReportSchemas"
                  :is-condensed-layout="
                    props.configurationLevel === 1 ? isCondensedLayout : props.isCondensedLayout
                  "
                  :global-collapsed="globalCollapsed"
                  :root-custom-report="props.rootCustomReport"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                />

                <!-- Json Type (Custom Config) -->
                <JsonCustomReportConfiguration
                  v-else-if="field.field_type === CustomReportFieldType.Json"
                  :customReportSchemas="props.customReportSchemas"
                  v-model="configuration"
                  :editor-schema="
                    CustomReportJsonReportConfigurationService.getJsonEditorSchemaForCustomReport()
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @calculation-type-changed="handleCustomReportTypeChanged"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="true"
                />

                <!-- Default fallback for unknown field types -->
                <JsonCustomReportConfiguration
                  v-else
                  :customReportSchemas="props.customReportSchemas"
                  v-model="configuration[field.name]"
                  :editor-schema="
                    CustomReportJsonReportConfigurationService.getJsonEditorSchemaForField(field)
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="false"
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>

    <div v-else class="flex h-full items-center justify-center text-gray-500">
      <div class="mt-8 text-center">
        <p class="mb-2 text-lg">No Custom Report Field Selected</p>
        <p class="text-sm">Select a field to configure its settings</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .collapsed-customReport-configuration {
    display: none;
  }

  .customReport-configuration {
    padding: 10px;
    border-radius: 4px;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  .object-customReport-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .object-customReport-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .object-customReport-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 110px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-customReport-builder-grid-span,
  .object-customReport-builder-grid-span,
  .json-customReport-configuration-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }
</style>
